import { useState, useRef, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useChatStore } from '../stores/chat';
import { api } from '../services/api';
import { Send, Loader2, Sparkles, User, Bot, RotateCcw } from 'lucide-react';
import clsx from 'clsx';
import { format } from 'date-fns';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

export function Chat() {
  const queryClient = useQueryClient();
  const {
    messages,
    isLoading,
    conversationId,
    addMessage,
    setLoading,
    setConversationId,
    clearMessages,
  } = useChatStore();
  const [input, setInput] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Auto-resize textarea
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.style.height = 'auto';
      inputRef.current.style.height = `${Math.min(inputRef.current.scrollHeight, 200)}px`;
    }
  }, [input]);

  const handleSend = async () => {
    const message = input.trim();
    if (!message || isLoading) return;

    addMessage({ role: 'user', content: message });
    setInput('');
    setLoading(true);

    try {
      const response = await api.sendMessage(message, conversationId || undefined);
      
      addMessage({
        role: 'assistant',
        content: response.message,
        agentUsed: response.agentUsed,
        actions: response.actions,
      });

      if (response.conversationId) {
        setConversationId(response.conversationId);
      }

      // Invalidate relevant queries based on which agents were used
      if (response.agentUsed) {
        const agents = response.agentUsed.toLowerCase();
        
        // When using bedrock-agent, we don't know which action group was invoked
        // So invalidate all queries that could have been affected
        if (agents.includes('bedrock-agent') || agents.includes('bedrock')) {
          queryClient.invalidateQueries({ queryKey: ['tasks'] });
          queryClient.invalidateQueries({ queryKey: ['events'] });
          queryClient.invalidateQueries({ queryKey: ['planner'] });
          queryClient.invalidateQueries({ queryKey: ['personalData'] });
        } else {
          // Manual orchestration mode - selective invalidation
          if (agents.includes('task')) {
            queryClient.invalidateQueries({ queryKey: ['tasks'] });
          }
          if (agents.includes('calendar')) {
            queryClient.invalidateQueries({ queryKey: ['events'] });
          }
          if (agents.includes('planner')) {
            queryClient.invalidateQueries({ queryKey: ['planner'] });
          }
        }
      }
    } catch (error) {
      addMessage({
        role: 'assistant',
        content: `Sorry, I encountered an error: ${error instanceof Error ? error.message : 'Unknown error'}`,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-border px-6 py-4">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-accent-muted">
            <Sparkles className="h-5 w-5 text-accent" />
          </div>
          <div>
            <h2 className="font-semibold">Personal Assistant</h2>
            <p className="text-sm text-text-tertiary">Ask me anything</p>
          </div>
        </div>
        {messages.length > 0 && (
          <button
            onClick={clearMessages}
            className="btn-ghost text-text-tertiary hover:text-text-secondary"
          >
            <RotateCcw className="h-4 w-4" />
            <span>Clear</span>
          </button>
        )}
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-auto p-6">
        {messages.length === 0 ? (
          <div className="flex h-full flex-col items-center justify-center text-center">
            <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-accent-muted">
              <Sparkles className="h-8 w-8 text-accent" />
            </div>
            <h3 className="text-lg font-semibold">How can I help you today?</h3>
            <p className="mt-2 max-w-sm text-sm text-text-tertiary">
              I can help you manage tasks, schedule events, plan your day, and remember important information.
            </p>
            <div className="mt-6 flex flex-wrap justify-center gap-2">
              {[
                'Create a task for tomorrow',
                'What\'s on my calendar today?',
                'Help me plan my week',
                'Remember that I prefer morning meetings',
              ].map((suggestion) => (
                <button
                  key={suggestion}
                  onClick={() => setInput(suggestion)}
                  className="rounded-full border border-border bg-surface px-4 py-2 text-sm text-text-secondary hover:border-accent/50 hover:text-text-primary"
                >
                  {suggestion}
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div className="space-y-6">
            {messages.map((message) => (
              <div
                key={message.id}
                className={clsx(
                  'flex gap-4',
                  message.role === 'user' ? 'flex-row-reverse' : ''
                )}
              >
                <div
                  className={clsx(
                    'flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg',
                    message.role === 'user' ? 'bg-accent-muted' : 'bg-surface-hover'
                  )}
                >
                  {message.role === 'user' ? (
                    <User className="h-4 w-4 text-accent" />
                  ) : (
                    <Bot className="h-4 w-4 text-text-secondary" />
                  )}
                </div>
                <div
                  className={clsx(
                    'flex max-w-[70%] flex-col',
                    message.role === 'user' ? 'items-end' : 'items-start'
                  )}
                >
                  <div
                    className={clsx(
                      'rounded-2xl px-4 py-3',
                      message.role === 'user'
                        ? 'bg-accent text-white'
                        : 'bg-surface-hover text-text-primary'
                    )}
                  >
                    {message.role === 'user' ? (
                      <p className="whitespace-pre-wrap text-sm">{message.content}</p>
                    ) : (
                      <div className="prose prose-sm prose-invert max-w-none text-text-primary prose-headings:text-text-primary prose-p:text-text-primary prose-strong:text-text-primary prose-ul:text-text-primary prose-ol:text-text-primary prose-li:text-text-primary prose-code:bg-surface prose-code:text-accent prose-pre:bg-surface">
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>
                          {message.content}
                        </ReactMarkdown>
                      </div>
                    )}
                  </div>
                  <div className="mt-1 flex items-center gap-2 text-2xs text-text-tertiary">
                    <span>{format(message.timestamp, 'HH:mm')}</span>
                    {message.agentUsed && (
                      <>
                        <span>•</span>
                        <span className="text-accent">{message.agentUsed}</span>
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))}
            {isLoading && (
              <div className="flex gap-4">
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-surface-hover">
                  <Bot className="h-4 w-4 text-text-secondary" />
                </div>
                <div className="flex items-center gap-2 rounded-2xl bg-surface-hover px-4 py-3">
                  <Loader2 className="h-4 w-4 animate-spin text-accent" />
                  <span className="text-sm text-text-tertiary">Thinking...</span>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
        )}
      </div>

      {/* Input */}
      <div className="border-t border-border p-4">
        <div className="flex items-end gap-3 rounded-xl border border-border bg-surface-hover p-3">
          <textarea
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Type your message..."
            rows={1}
            className="max-h-[200px] min-h-[24px] flex-1 resize-none bg-transparent text-sm text-text-primary outline-none placeholder:text-text-tertiary"
          />
          <button
            onClick={handleSend}
            disabled={isLoading || !input.trim()}
            className="flex h-8 w-8 items-center justify-center rounded-lg bg-accent text-white hover:bg-accent-hover disabled:opacity-50"
          >
            {isLoading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}
          </button>
        </div>
        <p className="mt-2 text-center text-2xs text-text-tertiary">
          Press <kbd className="kbd">Enter</kbd> to send, <kbd className="kbd">Shift+Enter</kbd> for new line
        </p>
      </div>
    </div>
  );
}

